/* AS84 record filter. Client-side faceted filtering for collection pages.
   Reads product tags like genre:Rock, decade:1970s, format:2LP, artist:Queen, label:EMI, condition:New.
   Values inside a group are OR, groups are AND. State lives in the URL so links, refresh and back all work. */
(() => {
  'use strict';

  const OWN_KEYS = ['find', 'stock', 'price', 'sort_by'];

  function init(root) {
    if (root.dataset.as84Ready) return;
    root.dataset.as84Ready = '1';

    let cfg;
    try { cfg = JSON.parse(root.querySelector('script[data-as84-config]').textContent); }
    catch (e) { console.error('[AS84] config error', e); return; }

    const grid = root.querySelector('[data-as84-grid]');
    if (!grid) return;
    const q1 = (sel) => root.querySelector(sel);
    const GROUPS = (cfg.groups || []).filter((g) => g.key && g.key !== '_end');
    const GKEYS = GROUPS.map((g) => g.key);
    const PAGE = cfg.pageSize || 24;
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const money = (n) => '$' + (Number.isInteger(n) ? n : n.toFixed(2));

    /* ---------- value clean-up ---------- */
    const FIX = new Map();
    String(cfg.fixes || '').split(/\r?\n/).forEach((line) => {
      const m = line.match(/^\s*([a-z]+)\s*:\s*(.+?)\s*=\s*(.*?)\s*$/i);
      if (m) FIX.set(m[1].toLowerCase() + ':' + m[2].toLowerCase(), m[3]);
    });
    const canon = {};
    function clean(k, raw) {
      let v = String(raw).replace(/\s+/g, ' ').trim();
      if (k === 'label') v = v.replace(/\s+[–-]\s+\S*\d\S*$/, '').replace(/\s+\(\d+\)$/, '');
      const key = k + ':' + v.toLowerCase();
      if (FIX.has(key)) v = FIX.get(key);
      if (!v) return '';
      canon[k] = canon[k] || new Map();
      const lc = v.toLowerCase();
      if (!canon[k].has(lc)) canon[k].set(lc, v);
      return canon[k].get(lc);
    }

    /* ---------- read products ---------- */
    const items = [...grid.querySelectorAll('.as84-item')].map((el) => {
      const d = el.dataset;
      const it = { el, i: +d.i || 0, p: +d.p || 0, c: +d.c || 0, s: d.s === '1', n: +d.n || 0, ti: d.ti || '', v: {}, artist: '' };
      GKEYS.forEach((k) => (it.v[k] = []));
      const words = [it.ti, d.ty || ''];
      (d.tags || '').split('|').forEach((tag) => {
        const ix = tag.indexOf(':');
        if (ix < 1) { words.push(tag); return; }
        const k = tag.slice(0, ix).trim().toLowerCase();
        const raw = tag.slice(ix + 1);
        words.push(raw);
        const v = clean(k, raw);
        if (!v) return;
        if (k === 'artist' && !it.artist) it.artist = v;
        if (it.v[k] && !it.v[k].includes(v)) it.v[k].push(v);
      });
      if (d.pl === '1' && it.v.condition) it.v.condition = ['Pre-loved'];
      it.hay = words.join(' ').toLowerCase();
      // show the cleaned artist name on the card
      el.querySelectorAll('.as84-artist').forEach((a) => { if (it.artist) a.textContent = it.artist; });
      return it;
    });
    const TOTAL = items.length;

    /* ---------- state ---------- */
    const params = new URLSearchParams(location.search);
    const S = { find: params.get('find') || '', stock: params.get('stock') === '1', pmin: null, pmax: null, shown: PAGE, sel: {}, refine: {}, all: {} };
    GROUPS.forEach((g) => {
      S.sel[g.key] = new Set(params.getAll(g.key).filter(Boolean));
      S.refine[g.key] = '';
      S.all[g.key] = false;
    });
    const priceVals = items.map((it) => it.p / 100);
    const LO = priceVals.length ? Math.floor(Math.min(...priceVals)) : 0;
    const HI = priceVals.length ? Math.ceil(Math.max(...priceVals)) : 0;
    const pr = params.get('price');
    if (pr && pr.includes('-')) {
      const [a, b] = pr.split('-');
      if (a !== '' && isFinite(+a) && +a > LO) S.pmin = Math.min(+a, HI);
      if (b !== '' && isFinite(+b) && +b < HI) S.pmax = Math.max(+b, LO);
    }

    const SORTS = {
      'created-descending': (a, b) => b.n - a.n || a.i - b.i,
      'created-ascending': (a, b) => a.n - b.n || a.i - b.i,
      'price-ascending': (a, b) => a.p - b.p || a.i - b.i,
      'price-descending': (a, b) => b.p - a.p || a.i - b.i,
      'title-ascending': (a, b) => a.ti.localeCompare(b.ti) || a.i - b.i,
      'title-descending': (a, b) => b.ti.localeCompare(a.ti) || a.i - b.i
    };
    const serverOrder = (a, b) => a.i - b.i;
    let sort = params.get('sort_by') || (cfg.urlSort ? cfg.urlSort : (cfg.allDefaultSort || cfg.serverSort)) || 'manual';
    if (!SORTS[sort] && sort !== cfg.serverSort) sort = cfg.serverSort;
    S.sort = sort;

    /* ---------- matching ---------- */
    let words = [];
    function match(it, skip) {
      if (words.length) { for (const w of words) if (!it.hay.includes(w)) return false; }
      if (skip !== 'stock' && S.stock && !it.s) return false;
      if (skip !== 'price') {
        if (S.pmin != null && it.p < S.pmin * 100) return false;
        if (S.pmax != null && it.p > S.pmax * 100) return false;
      }
      for (const g of GROUPS) {
        if (g.key === skip) continue;
        const sel = S.sel[g.key];
        if (sel.size && !it.v[g.key].some((v) => sel.has(v))) return false;
      }
      return true;
    }
    function counts(key) {
      const m = new Map();
      for (const it of items) if (match(it, key)) for (const v of it.v[key]) m.set(v, (m.get(v) || 0) + 1);
      return m;
    }
    const UNIVERSE = {};
    const VID = {};
    GROUPS.forEach((g) => {
      const s = new Set();
      items.forEach((it) => it.v[g.key].forEach((v) => s.add(v)));
      UNIVERSE[g.key] = [...s];
      VID[g.key] = new Map(UNIVERSE[g.key].map((v, n) => [v, n]));
    });
    // drop selections from the URL that don't exist here
    GROUPS.forEach((g) => S.sel[g.key].forEach((v) => { if (!VID[g.key].has(v)) S.sel[g.key].delete(v); }));

    /* ---------- build facet groups ---------- */
    const uid = (grid.dataset.id || 'x').replace(/[^\w-]/g, '');
    const chev = '<svg class="as84-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
    const facetsEl = q1('[data-as84-facets]');
    facetsEl.innerHTML = GROUPS.filter((g) => UNIVERSE[g.key].length).map((g) => {
      const open = g.open || S.sel[g.key].size > 0;
      return `<section class="as84-grp" data-grp="${g.key}">
        <div class="as84-grp__bar">
          <button type="button" class="as84-grp__btn" aria-expanded="${open}" aria-controls="as84-b-${uid}-${g.key}">${esc(g.label)}<span class="as84-badge" data-badge="${g.key}" hidden></span>${chev}</button>
          <button type="button" class="as84-clear" data-clear="${g.key}" hidden>Clear</button>
        </div>
        <div class="as84-grp__body" id="as84-b-${uid}-${g.key}"${open ? '' : ' hidden'}>
          ${g.long ? `<input type="search" class="as84-refine" data-refine="${g.key}" id="as84-r-${uid}-${g.key}" placeholder="Find ${esc(g.label.toLowerCase())}…" aria-label="Find ${esc(g.label.toLowerCase())}" autocomplete="off">` : ''}
          <div data-list="${g.key}"></div>
        </div>
      </section>`;
    }).join('');

    function orderValues(g, c, sel) {
      let vals;
      if (g.long) {
        vals = UNIVERSE[g.key].filter((v) => (c.get(v) || 0) > 0 || sel.has(v));
        const r = S.refine[g.key].trim().toLowerCase();
        if (r) vals = vals.filter((v) => v.toLowerCase().includes(r));
        vals.sort((x, y) => (sel.has(y) - sel.has(x)) || ((c.get(y) || 0) - (c.get(x) || 0)) || x.localeCompare(y));
      } else if (Array.isArray(g.order)) {
        const known = g.order.filter((v) => VID[g.key].has(v));
        const rest = UNIVERSE[g.key].filter((v) => !g.order.includes(v)).sort((x, y) => (c.get(y) || 0) - (c.get(x) || 0) || x.localeCompare(y));
        vals = known.concat(rest);
      } else if (g.order === 'numeric') {
        vals = UNIVERSE[g.key].slice().sort((x, y) => (parseInt(x, 10) || 9999) - (parseInt(y, 10) || 9999) || x.localeCompare(y));
      } else {
        vals = UNIVERSE[g.key].slice().sort((x, y) => ((c.get(y) || 0) - (c.get(x) || 0)) || x.localeCompare(y));
      }
      return vals;
    }

    function renderGroup(g) {
      const list = root.querySelector(`[data-list="${g.key}"]`);
      if (!list) return;
      const c = counts(g.key);
      const sel = S.sel[g.key];
      const vals = orderValues(g, c, sel);
      const refining = !!S.refine[g.key].trim();
      const expanded = !g.long || S.all[g.key] || refining;
      const show = expanded ? vals : vals.slice(0, 6);
      const lis = show.map((v) => {
        const n = c.get(v) || 0;
        const on = sel.has(v);
        const zero = !n && !on;
        const id = `as84-o-${uid}-${g.key}-${VID[g.key].get(v)}`;
        return `<li class="as84-opt${zero ? ' is-zero' : ''}"><label for="${id}"><input type="checkbox" id="${id}" data-g="${g.key}" value="${esc(v)}"${on ? ' checked' : ''}${zero ? ' disabled' : ''}><span class="as84-nm">${esc(v)}</span><span class="as84-ct">${n}</span></label></li>`;
      }).join('');
      let html = vals.length ? `<ul class="as84-opts${g.cols === 2 ? ' as84-opts--2' : ''}" role="list">${lis}</ul>` : `<p class="as84-nomatch">No ${esc(g.label.toLowerCase())} matches</p>`;
      if (g.long && S.all[g.key] && !refining) html = `<div class="as84-scroll">${html}</div>`;
      if (g.long && !refining && vals.length > 6) html += `<button type="button" class="as84-morelink" data-more="${g.key}">${S.all[g.key] ? 'Show fewer' : `Show all ${vals.length}`}</button>`;
      list.innerHTML = html;
      const badge = root.querySelector(`[data-badge="${g.key}"]`);
      if (badge) { badge.hidden = !sel.size; badge.textContent = sel.size; }
      const clr = root.querySelector(`[data-clear="${g.key}"]`);
      if (clr) clr.hidden = !sel.size;
    }

    /* ---------- price ---------- */
    const pminEl = q1('[data-as84-pmin]'), pmaxEl = q1('[data-as84-pmax]');
    const rminEl = q1('[data-as84-rmin]'), rmaxEl = q1('[data-as84-rmax]');
    const priceGrp = q1('[data-grp="price"]');
    if (priceGrp && HI <= LO) priceGrp.hidden = true;
    function syncPrice() {
      if (!rminEl) return;
      const a = S.pmin ?? LO, b = S.pmax ?? HI;
      [rminEl, rmaxEl].forEach((el) => { el.min = LO; el.max = HI; el.step = 1; });
      rminEl.value = a; rmaxEl.value = b;
      if (document.activeElement !== pminEl) pminEl.value = a;
      if (document.activeElement !== pmaxEl) pmaxEl.value = b;
      pminEl.placeholder = LO; pmaxEl.placeholder = HI;
      const pct = (v) => (HI === LO ? 0 : ((v - LO) / (HI - LO)) * 100);
      const fill = q1('[data-as84-pfill]');
      fill.style.left = pct(a) + '%'; fill.style.width = (pct(b) - pct(a)) + '%';
      q1('[data-as84-tlo]').textContent = money(LO);
      q1('[data-as84-thi]').textContent = money(HI);
      const on = S.pmin != null || S.pmax != null;
      q1('[data-as84-price-badge]').hidden = !on;
      q1('[data-as84-price-clear]').hidden = !on;
    }
    function setPrice(a, b) {
      a = Math.max(LO, Math.min(a, HI)); b = Math.max(LO, Math.min(b, HI));
      if (a > b) [a, b] = [b, a];
      S.pmin = a <= LO ? null : a;
      S.pmax = b >= HI ? null : b;
    }

    /* ---------- chips, url, results ---------- */
    function activeCount() {
      return (S.find ? 1 : 0) + (S.stock ? 1 : 0) + (S.pmin != null || S.pmax != null ? 1 : 0) + GROUPS.reduce((n, g) => n + S.sel[g.key].size, 0);
    }
    function chips() {
      const out = [];
      const chip = (rm, label, val, v) => `<button type="button" class="as84-chip" data-rm="${rm}"${v != null ? ` data-v="${esc(v)}"` : ''}>${label ? `<i>${esc(label)}</i> ` : ''}<b>${esc(val)}</b><span class="as84-x" aria-hidden="true">×</span><span class="visually-hidden">Remove</span></button>`;
      if (S.find) out.push(chip('find', 'Search', `“${S.find}”`));
      if (S.stock) out.push(chip('stock', '', 'In stock only'));
      if (S.pmin != null || S.pmax != null) out.push(chip('price', 'Price', `${money(S.pmin ?? LO)}–${money(S.pmax ?? HI)}`));
      GROUPS.forEach((g) => S.sel[g.key].forEach((v) => out.push(chip(g.key, g.label, v, v))));
      if (out.length) out.push('<button type="button" class="as84-chip as84-chip--all" data-rm="all">Clear all</button>');
      return out.join('');
    }
    function writeUrl() {
      const p = new URLSearchParams(location.search);
      OWN_KEYS.concat(GKEYS).forEach((k) => p.delete(k));
      p.delete('page');
      if (S.find) p.set('find', S.find);
      if (S.stock) p.set('stock', '1');
      if (S.pmin != null || S.pmax != null) p.set('price', `${S.pmin ?? ''}-${S.pmax ?? ''}`);
      GROUPS.forEach((g) => S.sel[g.key].forEach((v) => p.append(g.key, v)));
      if (S.sort && S.sort !== cfg.serverSort) p.set('sort_by', S.sort);
      else if (cfg.urlSort) p.set('sort_by', cfg.urlSort);
      const qs = p.toString();
      history.replaceState(history.state, '', location.pathname + (qs ? '?' + qs : '') + location.hash);
      return qs;
    }
    function filterQuery() {
      const p = new URLSearchParams();
      if (S.find) p.set('find', S.find);
      if (S.stock) p.set('stock', '1');
      GROUPS.forEach((g) => S.sel[g.key].forEach((v) => p.append(g.key, v)));
      return p.toString();
    }

    const countEl = q1('[data-as84-count]');
    const chipsEl = q1('[data-as84-chips]');
    const noneEl = q1('[data-as84-none]');
    const moreBtn = q1('[data-as84-more]');
    const moreNote = q1('[data-as84-more-note]');
    const goBtn = q1('[data-as84-go]');
    const openBadge = q1('[data-as84-open-badge]');
    const stockEl = q1('[data-as84-stock]');
    const stockCt = q1('[data-as84-stock-ct]');
    let lastOrder = items.map((it) => it.i).join(',');

    function render(opts = {}) {
      const focusId = document.activeElement && document.activeElement.id;
      words = S.find.toLowerCase().split(/\s+/).filter(Boolean);
      GROUPS.forEach(renderGroup);
      syncPrice();
      if (stockEl) {
        stockEl.checked = S.stock;
        stockCt.textContent = items.filter((it) => it.s && match(it, 'stock')).length;
      }

      const res = items.filter((it) => match(it)).sort(SORTS[S.sort] || serverOrder);
      const n = res.length;
      const shownSet = new Set(res.slice(0, S.shown));
      const resSet = new Set(res);
      const order = res.concat(items.filter((it) => !resSet.has(it)));
      const sig = order.map((it) => it.i).join(',');
      if (sig !== lastOrder) {
        const frag = document.createDocumentFragment();
        order.forEach((it) => frag.appendChild(it.el));
        grid.appendChild(frag);
        lastOrder = sig;
      }
      items.forEach((it) => { it.el.hidden = !shownSet.has(it); });
      res.slice(0, 8).forEach((it) => it.el.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; }));

      const active = activeCount();
      const word = n === 1 ? 'product' : 'products';
      countEl.textContent = active && n !== TOTAL ? `${n} of ${TOTAL} ${TOTAL === 1 ? 'product' : 'products'}` : `${n} ${word}`;
      if (goBtn) goBtn.textContent = n ? `Show ${n} ${word}` : 'No matches';
      const ch = chips();
      chipsEl.innerHTML = ch;
      if (openBadge) { openBadge.hidden = !active; openBadge.textContent = active; }

      if (noneEl) {
        noneEl.hidden = n > 0;
        grid.hidden = n === 0;
        if (!n) noneEl.querySelector('[data-as84-none-chips]').innerHTML = ch;
      }
      const left = n - S.shown;
      if (moreBtn) {
        moreBtn.hidden = left <= 0;
        moreBtn.textContent = `Show ${Math.min(PAGE, Math.max(left, 0))} more`;
        moreNote.hidden = left <= 0;
        moreNote.textContent = `Showing ${Math.min(S.shown, n)} of ${n}`;
      }
      if (!opts.keepUrl) writeUrl();
      if (focusId && document.getElementById(focusId) && document.activeElement !== document.getElementById(focusId)) {
        document.getElementById(focusId).focus({ preventScroll: true });
      }
    }
    const reset = () => { S.shown = PAGE; };
    const scrollToTop = () => {
      const top = root.getBoundingClientRect().top + window.scrollY - 90;
      if (window.scrollY > top + 200) window.scrollTo({ top, behavior: 'smooth' });
    };

    /* ---------- events ---------- */
    root.addEventListener('change', (e) => {
      const t = e.target;
      if (t.matches('.as84-opt input')) {
        const s = S.sel[t.dataset.g];
        t.checked ? s.add(t.value) : s.delete(t.value);
        reset(); render();
      } else if (t.matches('[data-as84-stock]')) {
        S.stock = t.checked; reset(); render();
      } else if (t.matches('[data-as84-pmin], [data-as84-pmax]')) {
        setPrice(pminEl.value === '' ? LO : +pminEl.value, pmaxEl.value === '' ? HI : +pmaxEl.value);
        reset(); render();
      } else if (t.matches('[data-as84-sort]')) {
        const v = t.value;
        if (!SORTS[v] && v !== cfg.serverSort) {
          // needs the server's order (Featured / Best selling): reload with the same filters
          const p = new URLSearchParams(writeUrl());
          p.set('sort_by', v);
          location.search = p.toString();
          return;
        }
        S.sort = v; reset(); render();
      }
    });
    root.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('[data-as84-pmin], [data-as84-pmax]')) { e.preventDefault(); e.target.blur(); }
    });

    let rangeTimer;
    [rminEl, rmaxEl].forEach((el) => el && el.addEventListener('input', () => {
      let a = +rminEl.value, b = +rmaxEl.value;
      if (a > b) { if (el === rminEl) a = b; else b = a; }
      setPrice(a, b);
      pminEl.value = S.pmin ?? LO; pmaxEl.value = S.pmax ?? HI;
      syncPrice();
      clearTimeout(rangeTimer);
      rangeTimer = setTimeout(() => { reset(); render(); }, 150);
    }));

    const qEl = q1('[data-as84-q]'), qx = q1('[data-as84-qx]');
    if (qEl) {
      qEl.value = S.find; qx.hidden = !S.find;
      let qTimer;
      qEl.addEventListener('input', () => {
        qx.hidden = !qEl.value;
        clearTimeout(qTimer);
        qTimer = setTimeout(() => { S.find = qEl.value.trim(); reset(); render(); }, 180);
      });
      qEl.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); clearTimeout(qTimer); S.find = qEl.value.trim(); reset(); render(); } });
      qx.addEventListener('click', () => { qEl.value = ''; qx.hidden = true; S.find = ''; reset(); render(); qEl.focus(); });
    }

    root.addEventListener('input', (e) => {
      const t = e.target;
      if (t.matches('[data-refine]')) {
        S.refine[t.dataset.refine] = t.value;
        renderGroup(GROUPS.find((g) => g.key === t.dataset.refine));
      }
    });

    root.addEventListener('click', (e) => {
      const cat = e.target.closest('a[data-as84-cat]');
      if (cat) {
        const fq = filterQuery();
        if (fq) { e.preventDefault(); location.href = cat.getAttribute('href') + (cat.getAttribute('href').includes('?') ? '&' : '?') + fq; }
        return;
      }
      const t = e.target.closest('button');
      if (!t || !root.contains(t)) return;
      if (t.classList.contains('as84-grp__btn')) {
        const open = t.getAttribute('aria-expanded') !== 'true';
        t.setAttribute('aria-expanded', String(open));
        const body = document.getElementById(t.getAttribute('aria-controls'));
        if (body) body.hidden = !open;
      } else if (t.dataset.clear) {
        S.sel[t.dataset.clear].clear(); reset(); render();
      } else if (t.matches('[data-as84-price-clear]')) {
        S.pmin = S.pmax = null; reset(); render();
      } else if (t.dataset.more) {
        S.all[t.dataset.more] = !S.all[t.dataset.more];
        renderGroup(GROUPS.find((g) => g.key === t.dataset.more));
      } else if (t.dataset.rm) {
        const k = t.dataset.rm;
        if (k === 'all') clearAll();
        else if (k === 'find') { S.find = ''; if (qEl) { qEl.value = ''; qx.hidden = true; } }
        else if (k === 'stock') S.stock = false;
        else if (k === 'price') S.pmin = S.pmax = null;
        else if (S.sel[k]) S.sel[k].delete(t.dataset.v);
        reset(); render();
      } else if (t.matches('[data-as84-more]')) {
        S.shown += PAGE; render({ keepUrl: true });
      } else if (t.matches('[data-as84-clearall]')) {
        clearAll(); reset(); render();
      } else if (t.matches('[data-as84-open]')) {
        drawer(true);
      } else if (t.matches('[data-as84-close], [data-as84-go]')) {
        drawer(false);
        if (t.matches('[data-as84-go]')) scrollToTop();
      }
    });

    function clearAll() {
      S.find = ''; if (qEl) { qEl.value = ''; qx.hidden = true; }
      S.stock = false; S.pmin = S.pmax = null;
      GROUPS.forEach((g) => S.sel[g.key].clear());
    }

    /* ---------- mobile drawer ---------- */
    const side = q1('[data-as84-side]');
    const scrim = root.parentElement.querySelector('[data-as84-scrim]');
    const openBtn = q1('[data-as84-open]');
    function drawer(on) {
      side.classList.toggle('is-open', on);
      if (scrim) scrim.hidden = !on;
      openBtn.setAttribute('aria-expanded', String(on));
      document.body.style.overflow = on ? 'hidden' : '';
      if (on) setTimeout(() => { const b = side.querySelector('[data-as84-close]'); b && b.focus(); }, 80);
      else openBtn.focus({ preventScroll: true });
    }
    if (scrim) scrim.addEventListener('click', () => drawer(false));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && side.classList.contains('is-open')) drawer(false); });
    window.matchMedia('(min-width: 990px)').addEventListener('change', (m) => { if (m.matches && side.classList.contains('is-open')) drawer(false); });

    /* keep the sticky sidebar clear of a sticky header */
    const headerSection = document.querySelector('.section-header');
    if (headerSection) {
      let ticking = false;
      const desk = window.matchMedia('(min-width: 990px)');
      const place = () => {
        ticking = false;
        if (!desk.matches) { side.style.top = ''; side.style.maxHeight = ''; return; }
        const r = headerSection.getBoundingClientRect();
        const covering = r.bottom > 0 && getComputedStyle(headerSection).position === 'sticky';
        side.style.top = (covering ? Math.round(r.bottom) + 16 : 16) + 'px';
        side.style.maxHeight = `calc(100vh - ${(covering ? Math.round(r.bottom) : 0) + 32}px)`;
      };
      window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(place); } }, { passive: true });
      place();
      window.addEventListener('load', place);
      window.addEventListener('resize', place);
      setTimeout(place, 600);
    }

    /* ---------- sort select ---------- */
    const sortEl = q1('[data-as84-sort]');
    if (sortEl) {
      if (![...sortEl.options].some((o) => o.value === S.sort)) {
        const o = document.createElement('option'); o.value = S.sort; o.textContent = (S.sort.charAt(0).toUpperCase() + S.sort.slice(1)).replace(/-/g, ' '); sortEl.appendChild(o);
      }
      sortEl.value = S.sort;
    }

    render({ keepUrl: !location.search });
  }

  const boot = () => document.querySelectorAll('[data-as84]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
